import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import { Alert, Button, Checkbox, FormControlLabel, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { client } from '@frontend/features/common/services';
import { HotelPreparation } from './HotelPreparation';
import { Candidate, CandidatePicker } from './AddSelection';
import { catalogAdminKey, catalogAdminPath } from './queries';
interface Source {
  connector: 'travelgate' | 'hotelbeds';
  supplier_hotel_id: number;
  connector_account_id: string;
  supplier_hotel_code: string;
  supplier_code: string | null;
  access_code: string | null;
  display_name: string;
  country_code: string | null;
  locality: string | null;
  current_hotel_id: string | null;
}
export function ReviewSources({
  scope,
  onBusyChange,
}: {
  scope?: { connector: 'travelgate' | 'hotelbeds'; account: string; access?: string; code: string };
  onBusyChange?(busy: boolean): void;
}) {
  const { t } = useTranslation();
  const connector = scope?.connector ?? 'travelgate';
  const [account, setAccount] = useState(scope?.account ?? ''),
    [q, setQ] = useState(scope?.code ?? ''),
    [search, setSearch] = useState(scope?.code ?? ''),
    [offset, setOffset] = useState(0);
  const [preparationBusy, setPreparationBusy] = useState(false);
  const [source, setSource] = useState<Source | null>(null),
    [candidate, setCandidate] = useState<Candidate | null>(null),
    [createNew, setCreateNew] = useState(false),
    [reason, setReason] = useState(''),
    [hotelId, setHotelId] = useState(''),
    [checkedSince, setCheckedSince] = useState(0);
  const accounts = useQuery(
    [...catalogAdminKey, 'import-accounts', connector],
    async () =>
      (
        await client.get<{ data: Array<{ connector_account_id: string; display_name: string }> }>(
          'hotel-catalog-admin/import-accounts',
          { params: { connector } },
        )
      ).data.data,
    { retry: false },
  );
  const page = useQuery(
    [...catalogAdminKey, 'supplier-hotels', account, search, offset],
    async () =>
      (
        await client.get<{ data: Source[]; meta: { count: number } }>('hotel-catalog-admin/supplier-hotels', {
          params: { connector_account_id: account, ...(search ? { search } : {}), offset },
        })
      ).data,
    { enabled: !!account, retry: false },
  );
  const eligibility = useQuery(
    [...catalogAdminKey, 'eligibility', hotelId],
    async () =>
      (
        await client.get<{
          data: { status: string; active_supplier_links_count: number; projection_rebuilt_at: string };
        }>(`hotel-catalog-admin/hotels/${encodeURIComponent(hotelId)}/eligibility`)
      ).data.data,
    {
      enabled: !!hotelId && !scope,
      retry: false,
      refetchInterval: (data) =>
        Date.now() - checkedSince < 60000 &&
        (!data || data.status !== 'active' || data.active_supplier_links_count === 0)
          ? 3000
          : false,
      refetchIntervalInBackground: false,
    },
  );
  const linking = useRef(false);
  const link = useMutation(
    async () => {
      if (!source || (!createNew && !candidate) || !reason.trim()) throw new Error();
      return (
        await client.post<{ data: { hotel_id: string } }>('hotel-catalog-admin/supplier-hotels/link-or-create', {
          connector_account_id: source.connector_account_id,
          supplier_hotel_code: source.supplier_hotel_code,
          supplier_hotel_id: source.supplier_hotel_id,
          ...(!createNew ? { hotel_id: candidate!.id } : {}),
          reason: reason.trim(),
        })
      ).data.data;
    },
    {
      retry: false,
      onSettled: () => {
        linking.current = false;
      },
      onSuccess: (data) => {
        setHotelId(data.hotel_id);
        setCheckedSince(Date.now());
        page.refetch();
      },
    },
  );
  const activate = useMutation(
    async () => {
      await client.post(`hotel-catalog-admin/hotels/${encodeURIComponent(hotelId)}/activate`, {
        reason: reason.trim(),
      });
    },
    {
      onSuccess: () => {
        setCheckedSince(Date.now());
        eligibility.refetch();
      },
    },
  );
  const busy = link.isLoading || activate.isLoading || (!!scope && link.isError) || preparationBusy;
  useEffect(() => {
    onBusyChange?.(busy);
    return () => onBusyChange?.(false);
  }, [busy, onBusyChange]);
  const sources = (page.data?.data ?? []).filter(
    (s) =>
      !scope ||
      (s.connector_account_id === scope.account &&
        s.connector === scope.connector &&
        (scope.connector === 'hotelbeds' || s.access_code === scope.access) &&
        s.supplier_hotel_code === scope.code),
  );
  const reset = () => {
    setSource(null);
    setCandidate(null);
    setCreateNew(false);
    setReason('');
    setHotelId('');
    link.reset();
    activate.reset();
  };
  return (
    <Stack spacing={2} sx={{ my: 3 }}>
      <Typography variant="h5">{t('hotelOnboarding.reviewSources')}</Typography>
      <Alert severity="info">{t('hotelOnboarding.sourceNotice')}</Alert>
      <TextField
        select
        label={t('hotelOnboarding.account')}
        value={account}
        disabled={busy || !!scope}
        onChange={(e) => {
          setAccount(e.target.value);
          setOffset(0);
          reset();
        }}
      >
        {(accounts.data ?? []).map((a) => (
          <MenuItem key={a.connector_account_id} value={a.connector_account_id}>
            {a.display_name} · {a.connector_account_id}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        label={t('hotelOnboarding.sourceSearch')}
        value={q}
        inputProps={{ maxLength: 128 }}
        disabled={busy || !!scope}
        onChange={(e) => setQ(e.target.value)}
      />
      <Button
        disabled={!account || busy || !!scope}
        onClick={() => {
          setSearch(q.trim());
          setOffset(0);
          reset();
          page.refetch();
        }}
      >
        {t('hotelOnboarding.find')}
      </Button>
      {(accounts.isError || page.isError) && <Alert severity="error">{t('hotelOnboarding.loadError')}</Alert>}
      {scope && (
        <Alert severity="info">
          {scope.account}
          {scope.access && ` / ${scope.access}`} / {scope.code} ·{' '}
          {t(scope.connector === 'hotelbeds' ? 'hotelCodeImport.directSourceHelp' : 'hotelCodeImport.sourceHelp')}
        </Alert>
      )}
      {scope && !page.isFetching && !page.isError && sources.length === 0 && (
        <Alert severity="warning">{t('hotelCodeImport.noSourceOnPage')}</Alert>
      )}
      {scope && link.isError && (
        <Alert severity="warning">
          {t('hotelCodeImport.linkUncertain')}
          <Button
            onClick={() => {
              reset();
              page.refetch();
            }}
          >
            {t('hotelCatalogAdmin.reload')}
          </Button>
        </Alert>
      )}
      {sources.map((s) => (
        <Button
          disabled={busy || (!!scope && (page.isFetching || page.isError))}
          key={s.supplier_hotel_id}
          variant={source?.supplier_hotel_id === s.supplier_hotel_id ? 'contained' : 'outlined'}
          onClick={() => {
            reset();
            setSource(s);
            if (s.current_hotel_id) {
              setHotelId(s.current_hotel_id);
              setCheckedSince(Date.now());
            }
          }}
        >
          {s.display_name} · {s.country_code} {s.locality} · {s.supplier_code} / {s.access_code} /{' '}
          {s.supplier_hotel_code} ({s.supplier_hotel_id})
        </Button>
      ))}
      <Stack direction="row">
        <Button
          disabled={!offset || busy || page.isFetching}
          onClick={() => {
            setOffset((v) => Math.max(0, v - 25));
            reset();
          }}
        >
          {t('hotelCatalogAdmin.previous')}
        </Button>
        <Button
          disabled={(page.data?.meta.count ?? 0) < 25 || busy || page.isFetching || offset >= 10000}
          onClick={() => {
            setOffset((v) => v + 25);
            reset();
          }}
        >
          {t('hotelCatalogAdmin.next')}
        </Button>
      </Stack>
      {source && (
        <Stack spacing={2} sx={{ pointerEvents: link.isLoading || activate.isLoading ? 'none' : undefined }}>
          <Typography>
            {source.display_name} · {source.supplier_code} / {source.access_code} · {source.current_hotel_id}
          </Typography>
          {!source.current_hotel_id && !hotelId && (
            <>
              <CandidatePicker
                kind="hotel"
                value={candidate}
                onChange={(v) => {
                  setCandidate(v);
                  setCreateNew(false);
                }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={createNew}
                    onChange={(_, v) => {
                      setCreateNew(v);
                      setCandidate(null);
                    }}
                  />
                }
                label={t('hotelOnboarding.createExplicit')}
              />
            </>
          )}
          <TextField
            label={t('hotelCatalogAdmin.reason')}
            value={reason}
            inputProps={{ maxLength: 512 }}
            onChange={(e) => setReason(e.target.value)}
          />
          {!source.current_hotel_id && !hotelId && (
            <Button
              disabled={!reason.trim() || (!candidate && !createNew) || busy}
              onClick={() => {
                if (linking.current || busy) return;
                linking.current = true;
                link.mutate();
              }}
            >
              {t(createNew ? 'hotelOnboarding.createFromSource' : 'hotelOnboarding.linkSource')}
            </Button>
          )}
          {link.isError && <Alert severity="error">{t('hotelOnboarding.linkError')}</Alert>}
          {scope && hotelId && (
            <HotelPreparation
              key={`${hotelId}:${source.supplier_hotel_id}`}
              hotelId={hotelId}
              sourceId={source.supplier_hotel_id}
              onBusyChange={setPreparationBusy}
            />
          )}
          {scope && hotelId && (
            <Button
              href={`${catalogAdminPath}/hotels/${encodeURIComponent(hotelId)}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('hotelCodeImport.openHotel')}
            </Button>
          )}
          {!scope && hotelId && (
            <>
              <Typography>
                {t('hotelOnboarding.canonicalHotel')}: {hotelId}
              </Typography>
              <Alert severity="info">
                {t('hotelOnboarding.agencyStatus')}:{' '}
                {eligibility.data?.status ?? t('hotelOnboarding.projectionPending')} ·{' '}
                {t('hotelOnboarding.activeLinks')}: {eligibility.data?.active_supplier_links_count ?? '—'}
              </Alert>
              <Typography>{t('hotelOnboarding.noAvailabilityClaim')}</Typography>
              <Button
                disabled={busy || !reason.trim() || eligibility.data?.status === 'active'}
                onClick={() => activate.mutate()}
              >
                {t('hotelOnboarding.activateAgency')}
              </Button>
              <Button
                disabled={busy}
                onClick={() => {
                  setCheckedSince(Date.now());
                  eligibility.refetch();
                  page.refetch();
                }}
              >
                {t('hotelOnboarding.refreshStatus')}
              </Button>
              {(eligibility.isError || activate.isError) && (
                <Alert severity="warning">{t('hotelOnboarding.activationPending')}</Alert>
              )}
            </>
          )}
        </Stack>
      )}
    </Stack>
  );
}
